import React, { useEffect, useMemo, useState } from 'react';
import { listBookings, updateBooking, deleteBooking } from '../api';
import { Button } from './ui/button';
import { useToast } from '../hooks/use-toast';
import { MessageCircle, Phone, Clock, Calendar, Trash2, CheckCircle2, XCircle, Loader2, Eye, RefreshCw, Star, UserRound } from 'lucide-react';
import BookingDetailModal, { StatusPill } from './BookingDetailModal';

function brl(n) { return `R$ ${Number(n || 0).toFixed(2).replace('.', ',')}`; }
function toYMD(d) { return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`; }
function fmtDateFull(ymd) {
  if (!ymd) return '';
  const [y, m, d] = ymd.split('-').map((x) => parseInt(x, 10));
  const dt = new Date(y, m - 1, d);
  const dias = ['Domingo', 'Segunda', 'Terça', 'Quarta', 'Quinta', 'Sexta', 'Sábado'];
  const meses = ['jan', 'fev', 'mar', 'abr', 'mai', 'jun', 'jul', 'ago', 'set', 'out', 'nov', 'dez'];
  return `${dias[dt.getDay()]}, ${d} ${meses[m - 1]}`;
}

const VIEWS = [
  { k: 'today', l: 'Hoje' },
  { k: 'tomorrow', l: 'Amanhã' },
  { k: 'week', l: 'Semana' },
  { k: 'upcoming', l: 'Próximos' },
];

export default function AgendaPanel({ slug }) {
  const [all, setAll] = useState([]);
  const [loading, setLoading] = useState(true);
  const [view, setView] = useState('today');
  const [detail, setDetail] = useState(null);
  const { toast } = useToast();

  const refresh = async () => {
    setLoading(true);
    try { const r = await listBookings(slug); setAll(r || []); }
    catch { setAll([]); }
    finally { setLoading(false); }
  };
  useEffect(() => { refresh(); }, [slug]);

  const setStatus = async (b, status) => {
    try { await updateBooking(slug, b.id, { status }); refresh(); toast({ title: 'Status atualizado' }); }
    catch { toast({ title: 'Erro ao atualizar' }); }
  };
  const remove = async (b) => {
    if (!window.confirm('Excluir este agendamento?')) return;
    try { await deleteBooking(slug, b.id); refresh(); } catch { /* ignore */ }
  };

  const today = toYMD(new Date());
  const tomorrow = toYMD(new Date(Date.now() + 86400000));

  const filtered = useMemo(() => {
    const base = (all || []).filter((b) => b.status !== 'cancelled');
    const sorted = (arr) => [...arr].sort((a, b) => (a.date + a.time).localeCompare(b.date + b.time));
    if (view === 'today') return sorted(base.filter((b) => b.date === today));
    if (view === 'tomorrow') return sorted(base.filter((b) => b.date === tomorrow));
    if (view === 'week') {
      const end = toYMD(new Date(Date.now() + 7 * 86400000));
      return sorted(base.filter((b) => b.date >= today && b.date <= end));
    }
    return sorted(base.filter((b) => b.date >= today));
  }, [all, view, today, tomorrow]);

  const nowStr = `${toYMD(new Date())} ${new Date().toTimeString().slice(0, 5)}`;
  const next = useMemo(() => {
    const list = (all || [])
      .filter((b) => b.status !== 'cancelled' && b.status !== 'completed')
      .filter((b) => `${b.date} ${b.time}` >= nowStr)
      .sort((a, b) => (a.date + a.time).localeCompare(b.date + b.time));
    return list[0] || null;
  }, [all, nowStr]);

  const whatsUrl = (b) => {
    const num = (b?.customerPhone || '').replace(/\D/g, '');
    const msg = encodeURIComponent(`Olá ${b?.customerName}! Confirmando seu horário de ${b?.serviceName} em ${b?.date} às ${b?.time}.`);
    return num ? `https://wa.me/${num}?text=${msg}` : '#';
  };

  const grouped = useMemo(() => {
    const g = {};
    filtered.forEach((b) => { (g[b.date] = g[b.date] || []).push(b); });
    return g;
  }, [filtered]);

  return (
    <div className="space-y-5">
      {/* NEXT CLIENT */}
      <div className="relative overflow-hidden bg-gradient-to-br from-neutral-900 to-neutral-700 text-white rounded-2xl p-5">
        <div className="absolute -right-10 -top-10 w-40 h-40 rounded-full bg-white/5" />
        <div className="relative">
          <div className="flex items-center gap-2 text-xs uppercase tracking-widest opacity-80"><Star size={12} /> Próximo cliente</div>
          {next ? (
            <>
              <div className="mt-2 flex items-start gap-4 flex-wrap">
                <div className="w-14 h-14 rounded-full bg-white/15 grid place-items-center text-xl font-bold">
                  {next.customerName?.[0]?.toUpperCase() || '?'}
                </div>
                <div className="flex-1 min-w-[200px]">
                  <div className="text-xl font-display font-bold leading-tight">{next.customerName}</div>
                  <div className="text-sm opacity-90">{next.serviceName}</div>
                  <div className="flex items-center gap-3 mt-1 text-xs opacity-80 flex-wrap">
                    <span className="inline-flex items-center gap-1"><Calendar size={11} /> {fmtDateFull(next.date)} • {next.time}</span>
                    <span className="inline-flex items-center gap-1"><Clock size={11} /> {next.durationMin} min</span>
                    <span className="inline-flex items-center gap-1"><Phone size={11} /> {next.customerPhone}</span>
                  </div>
                </div>
              </div>
              <div className="mt-4 flex flex-wrap gap-2">
                <Button onClick={() => setDetail(next)} className="gap-2 bg-white text-neutral-900 hover:bg-neutral-100"><Eye size={14} /> Ver cliente</Button>
                <a href={whatsUrl(next)} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 bg-emerald-500 hover:bg-emerald-600 text-white rounded-md h-10 px-4 font-semibold text-sm"><MessageCircle size={14} /> WhatsApp</a>
                {next.status !== 'confirmed' && <Button variant="outline" className="gap-2 bg-white/10 border-white/20 text-white hover:bg-white/20" onClick={() => setStatus(next, 'confirmed')}><CheckCircle2 size={14} /> Confirmar</Button>}
              </div>
            </>
          ) : (
            <div className="mt-2 opacity-80 text-sm">Nenhum agendamento próximo. Boa pausa! ☕</div>
          )}
        </div>
      </div>

      {/* FILTERS */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-1 bg-white rounded-full p-1 border border-neutral-200 overflow-x-auto">
          {VIEWS.map((v) => (
            <button key={v.k} onClick={() => setView(v.k)}
              className={`px-4 py-1.5 rounded-full text-sm font-medium transition whitespace-nowrap ${view === v.k ? 'bg-neutral-900 text-white' : 'text-neutral-600 hover:text-neutral-900'}`}>
              {v.l}
            </button>
          ))}
        </div>
        <Button variant="outline" size="sm" onClick={refresh} className="gap-1.5 border-neutral-300"><RefreshCw size={14} /> Atualizar</Button>
      </div>

      {/* LIST */}
      {loading ? (
        <div className="py-12 grid place-items-center"><Loader2 className="animate-spin text-neutral-400" /></div>
      ) : filtered.length === 0 ? (
        <div className="text-sm text-neutral-500 text-center py-16 bg-white rounded-2xl border border-dashed border-neutral-300">
          Nenhum agendamento neste período.
        </div>
      ) : (
        <div className="space-y-5">
          {Object.keys(grouped).sort().map((date) => (
            <div key={date}>
              <div className="text-xs font-semibold text-neutral-500 uppercase tracking-wider mb-2">{fmtDateFull(date)}</div>
              <div className="space-y-2">
                {grouped[date].map((b) => (
                  <div key={b.id} className="flex flex-wrap items-center gap-3 bg-white rounded-xl border border-neutral-200 p-3 text-sm">
                    <div className="text-center min-w-[52px] bg-neutral-100 rounded-lg py-1">
                      <div className="text-xs text-neutral-500">às</div>
                      <div className="font-mono font-bold text-neutral-900">{b.time}</div>
                    </div>
                    <div className="flex-1 min-w-[200px]">
                      <div className="flex items-center gap-2 flex-wrap">
                        <div className="font-semibold">{b.customerName}</div>
                        <StatusPill status={b.status} />
                      </div>
                      <div className="text-xs text-neutral-500 flex items-center gap-2 flex-wrap">
                        <span>{b.serviceName}</span><span>•</span>
                        <span>{b.durationMin}min</span>
                        {b.price > 0 && <><span>•</span><span>{brl(b.price)}</span></>}
                        <span>•</span><span className="font-mono">{b.customerPhone}</span>
                      </div>
                    </div>
                    <div className="flex items-center gap-1 flex-wrap">
                      <Button size="icon" variant="outline" className="h-8 w-8 border-neutral-300" onClick={() => setDetail(b)} title="Ver cliente"><Eye size={14} /></Button>
                      <a href={whatsUrl(b)} target="_blank" rel="noopener noreferrer" className="inline-flex items-center justify-center h-8 w-8 rounded-md border border-emerald-200 bg-emerald-50 text-emerald-700 hover:bg-emerald-100" title="WhatsApp"><MessageCircle size={14} /></a>
                      {b.status === 'pending' && <Button size="icon" variant="outline" className="h-8 w-8 border-neutral-300 text-emerald-600 hover:bg-emerald-50" onClick={() => setStatus(b, 'confirmed')} title="Confirmar"><CheckCircle2 size={14} /></Button>}
                      {b.status === 'confirmed' && <Button size="icon" variant="outline" className="h-8 w-8 border-neutral-300 text-blue-600 hover:bg-blue-50" onClick={() => setStatus(b, 'completed')} title="Concluir"><CheckCircle2 size={14} /></Button>}
                      {b.status !== 'cancelled' && b.status !== 'completed' && <Button size="icon" variant="outline" className="h-8 w-8 border-neutral-300 text-neutral-600 hover:bg-neutral-100" onClick={() => setStatus(b, 'cancelled')} title="Cancelar"><XCircle size={14} /></Button>}
                      <Button size="icon" variant="outline" className="h-8 w-8 border-neutral-300 text-red-600 hover:bg-red-50" onClick={() => remove(b)} title="Excluir"><Trash2 size={14} /></Button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}

      <BookingDetailModal booking={detail} onClose={() => setDetail(null)} />
    </div>
  );
}
